// Frontend-first video generator.
// For each post:
//   1. Generate 4 scene images via user's OpenAI key (gpt-image-1).
//   2. Render a 1080x1920 vertical video using Canvas + MediaRecorder.
//   3. Auto-download the .webm and surface it in the in-batch library.
// No backend rendering, no external API, no avatars, no voice-over.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Film, Wand2, Loader2, Download, Play, CheckCircle2, AlertTriangle, Music2,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { generateSceneImage } from "../lib/openai";
import { renderVideoFromScenes, triggerDownload } from "../lib/videoRenderer";

// ───── helpers ─────
const slugify = (s) =>
  (s || "video").slice(0, 50).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "video";

// MediaRecorder-produced .webm files are now patched with a real duration
// tag inside the videoRenderer (via fix-webm-duration), so the native HTML5
// video element gets clean metadata: native controls work, the seek bar is
// draggable, and replay-on-play works out of the box. The `onEnded` reset
// remains as belt-and-suspenders so a click on the play button after the
// video finishes always replays from the start.
const LibraryVideo = ({ src, ...rest }) => {
  const ref = useRef(null);
  const handleEnded = () => {
    const v = ref.current;
    if (!v) return;
    try { v.currentTime = 0; } catch (_) { /* ignore */ }
  };
  return (
    <video
      ref={ref}
      src={src}
      controls
      controlsList="nodownload"
      preload="metadata"
      playsInline
      onEnded={handleEnded}
      className="absolute inset-0 w-full h-full object-cover"
      {...rest}
    />
  );
};

const PHASE_LABEL = {
  idle: "Idle",
  images: "Generating scene images",
  preloading: "Preparing canvas",
  rendering: "Rendering video",
  done: "Ready",
  failed: "Failed",
};

// Per-post video job state — all in-memory, cleared when `posts` changes.
function initialJobs(posts) {
  return posts.map((p, i) => ({
    index: i,
    title: p.anchor_trend || `Post ${i + 1}`,
    phase: "idle",
    progress: 0,
    error: "",
    video_url: "",
    video_blob: null,
  }));
}

// ───── component ─────
export const BatchVideoGenerator = ({ posts = [], apiKey }) => {
  const [jobs, setJobs] = useState(() => initialJobs(posts));
  const [batchRunning, setBatchRunning] = useState(false);

  // Batch token — bumped every time `posts` changes. In-flight video jobs
  // from the previous batch check this and bail out, so regenerating scripts
  // while videos are rendering never blocks or leaks state.
  const batchIdRef = useRef(0);
  const abortRef = useRef({ aborted: false });

  // Reset the in-memory library every time the posts batch changes
  // (i.e. the user regenerated scripts).
  useEffect(() => {
    // Signal any in-flight runPost to stop, then bump the token.
    abortRef.current.aborted = true;
    batchIdRef.current += 1;
    abortRef.current = { aborted: false };

    // Revoke any existing blob URLs before dropping them.
    jobs.forEach((j) => { if (j.video_url) URL.revokeObjectURL(j.video_url); });
    setJobs(initialJobs(posts));
    setBatchRunning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts]);

  const completedCount = useMemo(
    () => jobs.filter((j) => j.phase === "done" && j.video_url).length,
    [jobs]
  );

  const updateJob = (idx, patch) => {
    setJobs((prev) => prev.map((j, i) => (i === idx ? { ...j, ...patch } : j)));
  };

  const buildSceneData = (post) => {
    const scenes = Array.isArray(post.scenes) ? post.scenes : [];
    return scenes.slice(0, 4).map((s) => ({
      // New schema (v17): `on_screen_text` is the burned-in caption (punchier
      // and shorter than the spoken `script`). Old schema fallbacks kept so
      // any legacy posts still in localStorage don't break the renderer.
      text: (s.on_screen_text || s.script || s.sentence || s.text || "").trim(),
      image_prompt: (s.image_prompt || s.pexels_query || "").trim(),
    }));
  };

  const runPost = async (idx) => {
    const post = posts[idx];
    if (!post) return;
    if (!apiKey) {
      toast.error("Connect your OpenAI key first");
      return;
    }
    const sceneSeeds = buildSceneData(post);
    if (sceneSeeds.length < 4) {
      toast.error("Post is missing scene data (need 4 scenes)");
      updateJob(idx, { phase: "failed", error: "Post has fewer than 4 scenes." });
      return;
    }
    if (sceneSeeds.some((s) => !s.image_prompt)) {
      toast.error("One or more scenes is missing an image prompt");
      updateJob(idx, { phase: "failed", error: "Missing image prompt on a scene." });
      return;
    }

    // Capture the batch token at the start. If `posts` is regenerated while
    // this job is running, the token bumps, our abort flag flips, and every
    // await-point bails out fast.
    const myBatchId = batchIdRef.current;
    const myAbort = abortRef.current;
    const isStale = () => myAbort.aborted || batchIdRef.current !== myBatchId;

    updateJob(idx, { phase: "images", progress: 0, error: "", video_url: "", video_blob: null });

    try {
      // 1. Generate 4 scene images IN PARALLEL — roughly 4x faster than
      // sequential and avoids the "later videos lagging" effect.
      let done = 0;
      const sceneUrls = await Promise.all(
        sceneSeeds.map(async (s) => {
          if (isStale()) return null;
          const { dataUrl } = await generateSceneImage({
            apiKey,
            prompt: s.image_prompt,
            quality: "low",
          });
          done += 1;
          if (!isStale()) {
            updateJob(idx, {
              phase: "images",
              progress: (done / sceneSeeds.length) * 0.5,
            });
          }
          return dataUrl;
        })
      );
      if (isStale()) return;

      // 2. Render video.
      const scenes = sceneSeeds.map((s, i) => ({
        text: s.text,
        image_url: sceneUrls[i],
      }));

      const { blob, url, mime } = await renderVideoFromScenes({
        scenes,
        sceneSeconds: 3.0, // 12s total — tighter pacing + faster recorder finalize
        onProgress: ({ phase, progress }) => {
          if (isStale()) return;
          const base = 0.5;
          const scaled = phase === "preloading" ? progress * 0.1 : 0.1 + progress * 0.4;
          updateJob(idx, { phase, progress: base + scaled });
        },
        isAborted: isStale,
      });
      if (isStale()) {
        try { URL.revokeObjectURL(url); } catch (_) {}
        return;
      }

      // Free the base64 scene image strings — they can be 2-5MB each.
      sceneUrls.length = 0;

      // The user explicitly requested NO auto-download — videos appear in
      // the in-batch library and download only when the user clicks the
      // per-video Download button.
      updateJob(idx, {
        phase: "done",
        progress: 1,
        video_url: url,
        video_blob: blob,
        video_mime: mime || "video/mp4",
      });
      toast.success(`Video ${idx + 1} ready`);
    } catch (e) {
      if (!isStale()) {
        updateJob(idx, { phase: "failed", error: e.message || "Video failed" });
        toast.error(`Video ${idx + 1} failed: ${e.message || "unknown"}`);
      }
    }
  };

  const runAll = async () => {
    if (batchRunning) return;
    setBatchRunning(true);
    try {
      const myBatchId = batchIdRef.current;
      const myAbort = abortRef.current;
      for (let i = 0; i < posts.length; i++) {
        if (myAbort.aborted || batchIdRef.current !== myBatchId) break;
        // eslint-disable-next-line no-await-in-loop
        await runPost(i);
      }
    } finally {
      setBatchRunning(false);
    }
  };

  const downloadOne = (job) => {
    // Use the actual mime returned by the renderer to pick the correct file
    // extension. MP4 is preferred; WebM only happens on browsers that don't
    // support WebCodecs (rare these days, but keep the fallback truthful).
    const ext = job.video_mime === "video/webm" ? "webm" : "mp4";
    const filename = `buddypass-${slugify(job.title)}-${job.index + 1}.${ext}`;
    // Create a FRESH blob URL each time to avoid stale / revoked URLs.
    // Fallback to the stored video_url if the blob reference was lost.
    try {
      if (job.video_blob) {
        const freshUrl = URL.createObjectURL(job.video_blob);
        triggerDownload(freshUrl, filename);
        setTimeout(() => URL.revokeObjectURL(freshUrl), 8000);
        return;
      }
    } catch (_) { /* fall through */ }
    if (job.video_url) {
      triggerDownload(job.video_url, filename);
    } else {
      toast.error("Video isn't ready yet");
    }
  };

  if (!posts?.length) return null;

  const totalProgress = jobs.length > 0
    ? jobs.reduce((s, j) => s + (j.phase === "done" ? 1 : j.progress || 0), 0) / jobs.length
    : 0;

  return (
    <section
      data-testid="video-generator"
      className="mt-10 rounded-3xl border border-white/10 bg-zinc-950/60 p-6 sm:p-8"
    >
      {/* Header + batch action */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
            <Film className="h-3 w-3" strokeWidth={2} /> In-browser video generator
          </div>
          <h3 className="font-display text-xl sm:text-2xl font-black text-white mt-2 tracking-tight">
            Turn each script into a <span className="text-amber-400">Viral Reel</span>
          </h3>
          <p className="text-xs text-zinc-500 mt-1.5">
            Turn each script into a vertical reel ready to upload.
          </p>
        </div>
        <Button
          type="button"
          onClick={runAll}
          disabled={batchRunning || !apiKey}
          data-testid="generate-all-videos"
          className="h-11 rounded-full bg-gradient-to-r from-emerald-500 via-cyan-500 to-violet-500 text-white font-semibold text-sm px-5 hover:brightness-110 disabled:opacity-50"
        >
          {batchRunning ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={2} /> Generating {completedCount}/{posts.length}…</>
          ) : (
            <><Wand2 className="mr-2 h-4 w-4" strokeWidth={2} /> Generate all {posts.length} videos</>
          )}
        </Button>
      </div>

      {/* Overall progress bar */}
      {batchRunning && (
        <div className="mb-6 h-1.5 w-full rounded-full bg-zinc-800 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-violet-400 transition-all duration-300"
            style={{ width: `${Math.min(100, totalProgress * 100).toFixed(1)}%` }}
          />
        </div>
      )}

      {/* Job cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {jobs.map((job, i) => {
          const post = posts[i];
          const busy = job.phase !== "idle" && job.phase !== "done" && job.phase !== "failed";
          return (
            <div
              key={i}
              data-testid={`video-job-${i}`}
              className="rounded-2xl border border-white/10 bg-zinc-900/70 p-4 space-y-3"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
                  Post {String(i + 1).padStart(2, "0")} · {post?.type || "—"}
                </span>
                {job.phase === "done" && (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" strokeWidth={2} />
                )}
                {job.phase === "failed" && (
                  <AlertTriangle className="h-4 w-4 text-red-400" strokeWidth={2} />
                )}
              </div>
              <div className="text-[13px] text-zinc-200 font-semibold leading-snug line-clamp-2 min-h-[2.6em]">
                {job.title}
              </div>

              {busy && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                    {PHASE_LABEL[job.phase] || job.phase}
                  </div>
                  <div className="h-1 w-full rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-violet-400 transition-all duration-200"
                      style={{ width: `${Math.min(100, (job.progress || 0) * 100).toFixed(1)}%` }}
                    />
                  </div>
                </div>
              )}

              {job.phase === "failed" && (
                <p className="text-[11px] text-red-300/90 leading-snug">{job.error}</p>
              )}

              <Button
                type="button"
                onClick={() => runPost(i)}
                disabled={busy || !apiKey}
                data-testid={`generate-video-${i}`}
                className="w-full h-9 rounded-full bg-amber-500 text-[#1c1302] font-semibold text-xs hover:bg-amber-400 disabled:opacity-50"
              >
                {busy ? (
                  <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" strokeWidth={2} /> Working…</>
                ) : job.phase === "done" ? (
                  <><Wand2 className="mr-2 h-3.5 w-3.5" strokeWidth={2} /> Re-generate</>
                ) : (
                  <><Wand2 className="mr-2 h-3.5 w-3.5" strokeWidth={2} /> Generate video</>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Your Video Library (current batch only — clears on re-generation) */}
      <div
        data-testid="your-video-library"
        className="rounded-2xl border border-white/10 bg-zinc-900/40 p-5"
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-violet-300">
            <Film className="h-3 w-3" strokeWidth={2} /> Your video library
          </div>
          <span className="text-[11px] text-zinc-500">
            {completedCount} of {jobs.length} ready · clears when you regenerate scripts
          </span>
        </div>

        {completedCount === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 bg-zinc-950/40 p-6 text-center">
            <Play className="h-6 w-6 mx-auto text-zinc-600 mb-2" strokeWidth={1.5} />
            <p className="text-xs text-zinc-500">
              Generate a video above and it will show up here for preview + download.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {jobs.map((job, i) => {
              const audio = posts[i]?.trending_audio_suggestion;
              return job.phase === "done" && job.video_url ? (
                <div
                  key={i}
                  data-testid={`library-video-${i}`}
                  className="rounded-xl border border-white/10 bg-zinc-950/70 p-2.5 space-y-2"
                >
                  <div className="relative rounded-lg overflow-hidden bg-black aspect-[9/16] border border-white/5">
                    <LibraryVideo
                      src={job.video_url}
                      data-testid={`library-video-player-${i}`}
                    />
                  </div>
                  <div className="text-[11px] text-zinc-300 font-medium leading-snug line-clamp-2 min-h-[2.4em] px-0.5">
                    {job.title}
                  </div>
                  {audio && audio.song && (
                    <div
                      data-testid={`library-audio-${i}`}
                      className="flex items-center gap-1.5 rounded-lg border border-pink-500/30 bg-pink-500/5 px-2 py-1.5 text-[11px] text-pink-200"
                      title={audio.why || ""}
                    >
                      <Music2 className="h-3 w-3 shrink-0" strokeWidth={2} />
                      <span className="truncate">
                        <span className="font-semibold">{audio.song}</span>
                        {audio.artist && <span className="text-pink-300/70"> · {audio.artist}</span>}
                      </span>
                    </div>
                  )}
                  <Button
                    type="button"
                    onClick={() => downloadOne(job)}
                    data-testid={`download-video-${i}`}
                    className="w-full h-8 rounded-full bg-emerald-500 text-black font-semibold text-[11px] hover:bg-emerald-400"
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={2.5} />
                    Download
                  </Button>
                </div>
              ) : null;
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default BatchVideoGenerator;
